import { INotificationSeverity } from '../../../../models/types';

// templateSeverity — nullable-enum: null/отсутствие = «наследовать важность от события».
// MtsDropdown сводит значение опции к строке (id.toString()), поэтому null нельзя отдать
// в поле напрямую — иначе не совпадёт ни одна опция и список окажется пустым. Вместо этого
// «Авто» представлено явным sentinel-значением, которое при сохранении отображается обратно в null.
// Справочник /notification/severity содержит systemName в PascalCase (System/Ignored/Low/Med/High/Critical),
// поэтому строчный sentinel никогда не пересечётся с реальным значением.
export const TEMPLATE_SEVERITY_AUTO = 'auto';
export const TEMPLATE_SEVERITY_AUTO_LABEL = 'Авто (из события)';
export const TEMPLATE_SEVERITY_AUTO_HINT = 'Важность сообщения наследуется от события';

interface ISeverityOption {
  id: string;
  name: string;
}

// Справочник EnumDescription[] → опции FormDropdown ({ id, name }[]).
// «Авто (из события)» идёт первой явной опцией и соответствует null.
// Значение sentinel зарезервировано за опцией «Авто»: если справочник вдруг вернёт
// systemName === sentinel, такое значение исключается — иначе получится дубль ключа/значения
// и неоднозначное сопоставление с «наследовать от события».
export const toSeverityOptions = (
  severities: INotificationSeverity[]
): ISeverityOption[] => [
  { id: TEMPLATE_SEVERITY_AUTO, name: TEMPLATE_SEVERITY_AUTO_LABEL },
  ...severities
    .filter(({ systemName }) => systemName !== TEMPLATE_SEVERITY_AUTO)
    .map(({ systemName, friendlyName }) => ({ id: systemName, name: friendlyName }))
];

// Загрузка: входящий templateSeverity (string | null | undefined) → значение поля формы.
// null/отсутствие/пусто → sentinel «Авто»; иначе — systemName как есть.
export const severityToFieldValue = (severity?: string | null): string =>
  severity || TEMPLATE_SEVERITY_AUTO;

// Сохранение: значение поля формы → исходящий templateSeverity.
// sentinel «Авто» (а также null/отсутствие/пусто) → null; иначе — systemName как есть.
export const fieldValueToSeverity = (value?: string | null): string | null =>
  !value || value === TEMPLATE_SEVERITY_AUTO ? null : value;

// Гарантирует присутствие выбранного значения среди опций: если хранимое systemName
// отсутствует в справочнике (переименовано/удалено на бэкенде), добавляем его как опцию,
// чтобы дропдаун не отображался пустым.
export const withSelectedOption = (
  options: ISeverityOption[],
  fieldValue: string | null | undefined
): ISeverityOption[] => {
  if (!fieldValue || fieldValue === TEMPLATE_SEVERITY_AUTO) return options;
  return options.some(option => option.id === fieldValue)
    ? options
    : [...options, { id: fieldValue, name: fieldValue }];
};

// Описание для текущего значения поля: «Авто» — подсказка о наследовании от события;
// известному systemName — description из справочника; неизвестному (нет в справочнике) —
// пометка, чтобы не вводить пользователя в заблуждение подсказкой про «Авто».
export const describeSeverity = (
  fieldValue: string | null | undefined,
  severities: INotificationSeverity[]
): string => {
  if (!fieldValue || fieldValue === TEMPLATE_SEVERITY_AUTO) return TEMPLATE_SEVERITY_AUTO_HINT;
  return (
    severities.find(item => item.systemName === fieldValue)?.description ??
    `Неизвестное значение: ${fieldValue}`
  );
};
